import { Box, ModalProps, useMediaQuery, VStack } from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import HoneyPurchaseContainer from "containers/HoneyPurchaseContainer";
import EthPurchaseContainer from "containers/EthPurchaseContainer";
import TraitTile from "components/TraitTile";
import { SwiperSlide } from "swiper/react/swiper-react";
import { Controller } from "swiper";
import ControlledSwiper from "components/ControlledSwiper";
import useTraitSales from "hooks/useTraitSales";
import SimplePopup from "components/SimplePopup";
import useCurrency from "hooks/useCurrency";
import { Currency } from "types";
import { useCallback } from "react";
import ScrollableArea from "components/ScrollableArea";

type PurchasePopupProps = Omit<ModalProps, "children"> & {
  traitsToBuy: number[];
  tokenId: number;
  onSuccess: () => void;
};

const PurchasePopup = ({
  tokenId,
  traitsToBuy,
  onSuccess,
  ...rest
}: PurchasePopupProps) => {
  const translate = useTranslate();
  const currency = useCurrency();
  const { traitSalesByTokenId } = useTraitSales();
  const isMultiple = traitsToBuy.length > 1;
  const [isHighEnough] = useMediaQuery("(min-height: 700px)");

  const slidesPerView = isMultiple ? 1.35 : 1;

  const renderTile = useCallback(
    (tokenId: number) => {
      const traitSale = traitSalesByTokenId[tokenId];

      return traitSale ? (
        <TraitTile
          key={tokenId}
          tokenId={tokenId}
          price={traitSale.price}
          supply={[traitSale.counter, traitSale.maxSupply]}
          w="full"
          canvasSize="12"
        />
      ) : null;
    },
    [traitSalesByTokenId]
  );

  return traitSalesByTokenId ? (
    <SimplePopup
      title={translate(
        isMultiple ? "purchase:title:multi" : "purchase:title:single",
        {
          count: traitsToBuy.length,
        }
      )}
      description={
        isHighEnough ? translate(`purchase:description:${currency}`) : undefined
      }
      hasScrollArea
      size="sm"
      {...rest}
    >
      {currency === Currency.Honey ? (
        <Box mt="4" mb="2">
          <ControlledSwiper
            spaceBetween={2}
            threshold={10}
            modules={[Controller]}
            slidesPerView={slidesPerView}
            freeMode
          >
            {traitsToBuy.map((tokenId) => (
              <SwiperSlide key={tokenId}>{renderTile(tokenId)}</SwiperSlide>
            ))}
          </ControlledSwiper>
        </Box>
      ) : (
        <ScrollableArea mt="6" mb="6">
          <VStack spacing="2px">{traitsToBuy.map(renderTile)}</VStack>
        </ScrollableArea>
      )}

      {currency === Currency.Honey ? (
        <HoneyPurchaseContainer
          tokenId={tokenId}
          traitsToBuy={traitsToBuy}
          onSuccess={onSuccess}
        />
      ) : (
        <EthPurchaseContainer
          tokenId={tokenId}
          traitsToBuy={traitsToBuy}
          onSuccess={onSuccess}
        />
      )}
    </SimplePopup>
  ) : null;
};

export default PurchasePopup;
